/**
 * useRealtimeExecutions Hook
 *
 * Real-time Firestore listener for execution updates.
 * Provides live execution stream with automatic cleanup.
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from '@/config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  QuerySnapshot,
  DocumentData
} from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { isInternalExecution } from '@simcheck/shared-types';
import { toDate } from '@/lib/date-utils';

export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface RealtimeExecution {
  id: string;
  flowId: string;
  flowName: string;
  status: ExecutionStatus;
  startedAt: Date | null;
  completedAt: Date | null;
  duration: number | null; // seconds
  modemId?: string;
  modemName?: string;
  locationId?: string;
  locationName?: string;
  userId: string;
  organizationId: string;
  error?: string;
  progress?: number; // 0-100 for running executions
}

interface UseRealtimeExecutionsOptions {
  limitCount?: number;
  statusFilter?: ExecutionStatus[];
  includeCompleted?: boolean;
}

interface UseRealtimeExecutionsResult {
  executions: RealtimeExecution[];
  runningCount: number;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for real-time execution updates via Firestore onSnapshot
 */
export function useRealtimeExecutions(
  options: UseRealtimeExecutionsOptions = {}
): UseRealtimeExecutionsResult {
  const { activeOrganization } = useAuth();
  const { limitCount = 10, statusFilter, includeCompleted = true } = options;

  const [executions, setExecutions] = useState<RealtimeExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeOrganization?.id) {
      setExecutions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Build query.
    // Over-fetched: internal/platform-operated runs are dropped below and
    // Firestore can't express "source not in (...)" alongside this orderBy.
    let executionsQuery = query(
      collection(db, 'executions'),
      where('organizationId', '==', activeOrganization.id),
      orderBy('startedAt', 'desc'),
      limit(limitCount * 2)
    );

    // Real-time listener
    const unsubscribe = onSnapshot(
      executionsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const executionsList: RealtimeExecution[] = [];

        snapshot.docs.forEach((doc) => {
          const data = doc.data();

          // Platform-operated runs (e.g. the admin "Discover Location" button)
          // are device-setup actions, not customer tests — never surface them
          // in a tenant's feed. See #267.
          if (isInternalExecution(data)) {
            return;
          }

          // Apply status filter if provided
          if (statusFilter && !statusFilter.includes(data.status)) {
            return;
          }

          // Skip completed if not included
          if (!includeCompleted && (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled')) {
            return;
          }

          // Parse timestamps
          const startedAt: Date | null = data.startedAt
            ? toDate(data.startedAt) ?? null
            : null;
          const completedAt: Date | null = data.completedAt
            ? toDate(data.completedAt) ?? null
            : null;

          // Calculate duration
          let duration: number | null = null;
          if (startedAt && completedAt) {
            duration = Math.round((completedAt.getTime() - startedAt.getTime()) / 1000);
          } else if (startedAt && data.status === 'running') {
            // For running executions, calculate current duration
            duration = Math.round((Date.now() - startedAt.getTime()) / 1000);
          }

          executionsList.push({
            id: doc.id,
            flowId: data.flowId || '',
            flowName: data.flowName || 'Unknown Flow',
            status: data.status || 'pending',
            startedAt,
            completedAt,
            duration,
            modemId: data.modemId,
            modemName: data.modemName,
            locationId: data.locationId,
            locationName: data.locationName,
            userId: data.userId || '',
            organizationId: data.organizationId || '',
            error: data.error,
            progress: data.progress,
          });
        });

        setExecutions(executionsList.slice(0, limitCount));
        setLoading(false);
      },
      (err) => {
        console.error('Real-time executions error:', err);
        setError('Failed to load real-time executions');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [activeOrganization?.id, limitCount, statusFilter, includeCompleted]);

  // Calculate running count
  const runningCount = executions.filter(e => e.status === 'running').length;

  return {
    executions,
    runningCount,
    loading,
    error,
  };
}

/**
 * Hook specifically for running executions (for dashboard indicator)
 */
export function useRunningExecutions(): UseRealtimeExecutionsResult {
  return useRealtimeExecutions({
    statusFilter: ['running', 'pending'],
    includeCompleted: false,
    limitCount: 20,
  });
}
